<template>
  <div :class="['oo-editor-wrap', { 'is-fullscreen': fullscreen }]">
    <div v-if="loading" class="oo-loading">{{ loadingHint || '正在加载 Word 原表…' }}</div>
    <div v-else-if="error" class="oo-error">{{ error }}</div>
    <div :id="editorId" :class="['oo-editor', { 'is-fullscreen': fullscreen }]"></div>
  </div>
</template>

<script>
import { loadOnlyOfficeApi, buildEditConfig } from '@/utils/onlyoffice'

let editorSeq = 0

export default {
  name: 'OnlyOfficeEditor',
  props: {
    config: {
      type: Object,
      default: null
    },
    fullscreen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    editorSeq += 1
    return {
      editorId: `oo-edit-${editorSeq}`,
      editor: null,
      loading: false,
      loadingHint: '',
      error: ''
    }
  },
  watch: {
    config: {
      immediate: true,
      handler(val) {
        if (!val || !val.document || !val.document.url) return
        // 只在首次挂载；后续换文档由 refreshDocument 处理，避免 config 更新触发整页重建
        if (!this.editor) {
          this.mountEditor(val)
        }
      }
    }
  },
  beforeDestroy() {
    this.destroyEditor()
  },
  methods: {
    destroyEditor() {
      if (this.editor) {
        try {
          this.editor.destroyEditor()
        } catch (e) { /* ignore */ }
        this.editor = null
      }
    },
    async mountEditor(rawConfig) {
      this.loading = true
      this.error = ''
      this.destroyEditor()
      try {
        const DocsAPI = await loadOnlyOfficeApi(rawConfig.documentServerUrl)
        const self = this
        self._wasDirty = false
        const cfg = buildEditConfig(rawConfig, {
          onDocumentReady() {
            self.$emit('ready')
          },
          onDocumentStateChange(event) {
            const dirty = !!(event && event.data)
            if (dirty) self._wasDirty = true
            if (self._wasDirty && !dirty) self.$emit('saved')
          }
        })
        await this.$nextTick()
        this.editor = new DocsAPI.DocEditor(this.editorId, cfg)
      } catch (e) {
        this.error = (e && e.message) || 'OnlyOffice 初始化失败'
      } finally {
        this.loading = false
      }
    },
    triggerForceSave() {
      if (!this.editor) return
      try {
        if (typeof this.editor.requestSave === 'function') {
          this.editor.requestSave()
          return
        }
      } catch (e) { /* ignore */ }
      const root = document.getElementById(this.editorId)
      const iframe = root && root.querySelector('iframe')
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(JSON.stringify({ message: 'forcesave' }), '*')
      }
    },
    /**
     * 在已打开的 Word 内按标签写入值（不 reload 文档）。
     * items: [{ label: '天平型号', value: '...', allLabels: ['天平型号', ...] }]
     */
    applyMergeLabels(items) {
      if (!this.editor || !items || !items.length) return Promise.resolve(false)
      if (typeof this.editor.createConnector !== 'function') return Promise.resolve(false)
      let connector
      try {
        connector = this.editor.createConnector()
      } catch (e) {
        return Promise.resolve(false)
      }
      if (!connector || typeof connector.callCommand !== 'function') return Promise.resolve(false)
      window.Asc = window.Asc || {}
      Asc.scope = Asc.scope || {}
      Asc.scope.mergeItems = items
      return new Promise(resolve => {
        let settled = false
        const done = ok => {
          if (settled) return
          settled = true
          resolve(!!ok)
        }
        const timer = setTimeout(() => done(false), 8000)
        try {
          connector.callCommand(function() {
            var items = Asc.scope.mergeItems || []
            var allLabels = (items[0] && items[0].allLabels) ? items[0].allLabels.slice() : []
            for (var ai = 0; ai < items.length; ai++) {
              var lb = items[ai].label
              if (lb && allLabels.indexOf(lb) < 0) allLabels.push(lb)
            }
            function replaceSlot(text, label, value) {
              if (value === null || value === undefined || String(value) === '') return text
              var markers = [label + '：', label + ':', label]
              var marker = ''
              var idx = -1
              for (var m = 0; m < markers.length; m++) {
                idx = text.indexOf(markers[m])
                if (idx >= 0) { marker = markers[m]; break }
              }
              if (idx < 0) return text
              var start = idx + marker.length
              var end = text.length
              for (var j = 0; j < allLabels.length; j++) {
                var sl = allLabels[j]
                if (!sl || sl === label) continue
                var variants = [sl + '：', sl + ':', sl]
                for (var v = 0; v < variants.length; v++) {
                  var p = text.indexOf(variants[v], start)
                  if (p >= 0 && p < end) end = p
                }
              }
              return text.substring(0, start) + String(value) + text.substring(end)
            }
            var doc = Api.GetDocument()
            var paras = doc.GetAllParagraphs()
            for (var pi = 0; pi < paras.length; pi++) {
              var text = paras[pi].GetText()
              if (!text) continue
              var newText = text
              var changed = false
              for (var ii = 0; ii < items.length; ii++) {
                var nt = replaceSlot(newText, items[ii].label, items[ii].value)
                if (nt !== newText) { newText = nt; changed = true }
              }
              if (changed) {
                paras[pi].RemoveAllElements()
                paras[pi].AddText(newText)
              }
            }
          }, () => {
            clearTimeout(timer)
            done(true)
          })
        } catch (e) {
          clearTimeout(timer)
          done(false)
        }
      })
    },
    /** 服务端合并 docx 前先关闭编辑器，避免 OnlyOffice 把旧空白内容保存回磁盘覆盖合并结果 */
    async prepareForServerMerge() {
      this.destroyEditor()
      await new Promise(resolve => setTimeout(resolve, 450))
    },
    /** 服务端已合并 docx 后重新打开（OnlyOffice 无 Connector 时的可靠方案） */
    async reloadMergedDocument(rawConfig, hint) {
      if (!rawConfig || !rawConfig.document || !rawConfig.document.url) return false
      this.loadingHint = hint || '正在更新 Word 表头…'
      this.loading = true
      this.error = ''
      try {
        await this.$nextTick()
        const doc = Object.assign({}, rawConfig.document)
        const base = String(doc.url || '')
        doc.url = base + (base.includes('?') ? '&' : '?') + '_v=' + Date.now()
        doc.key = (doc.key || 'doc') + '_' + Date.now()
        await this.mountEditor(Object.assign({}, rawConfig, { document: doc }))
        return true
      } catch (e) {
        this.error = (e && e.message) || 'Word 重新加载失败'
        return false
      } finally {
        this.loadingHint = ''
      }
    },
    /** 换 docx 内容时轻量刷新，避免整页销毁重建 */
    async refreshDocument(rawConfig) {
      if (!rawConfig || !rawConfig.document || !rawConfig.document.url) return false
      if (!this.editor) return false
      const doc = rawConfig.document
      const url = doc.url + (String(doc.url).includes('?') ? '&' : '?') + '_t=' + Date.now()
      const fileCfg = buildEditConfig(rawConfig).document
      fileCfg.url = url
      try {
        if (typeof this.editor.refreshFile === 'function') {
          this.editor.refreshFile({ document: fileCfg })
          return true
        }
      } catch (e) { /* ignore */ }
      return false
    }
  }
}
</script>

<style scoped>
.oo-editor-wrap {
  width: 100%;
  position: relative;
  background: #f0f0f0;
  border: 1px solid #dcdfe6;
}
.oo-editor-wrap:not(.is-fullscreen) {
  height: calc(100vh - 280px);
  min-height: 560px;
}
.oo-editor-wrap.is-fullscreen {
  height: 100%;
  min-height: 0;
  border: none;
  background: #fff;
}
.oo-editor {
  width: 100%;
  height: 100%;
}
.oo-loading,
.oo-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  background: #f0f0f0;
  color: #909399;
}
.oo-error {
  color: #f56c6c;
  padding: 24px;
  text-align: center;
}
</style>
